import { useEffect, useMemo, useRef, useState } from "react";
/**
 * useControlOrUnControl hook decides whether a prop
 * is leveraged in controllable or uncontrollable way for that particular
 * instance of the component. Furthermore it calls the change
 * handler based after updating the state value or when prop value
 * is updated.
 *
 * @param {Object} controlOrUncontrolPropsList A component will have multiple props that
 * differentiate the usage between Controlled vs Uncontrolled way, that
 * prop value has to be provided in the object against respective key.
 *    - unControlledPropValue - unControlled property default value.
 *    - controlledPropValue - controlled property value.
 *    - changeHandler - the handler to be triggered when the value is
 *      updated either internally or externally. It receives current or
 *      updated prop value and other arguments.
 * @return {Object} getter and setter methods for retrieving or updating
 * the prop/state value based on the usage of the prop with the component.
 *
 * @example
 *
 * function ParentComponent() {
 *
 *  const [getPropValue, setPropValue] = useControlOrUnControl(
 *    {
 *      unControlledPropValue: defaultSelectedTab,
 *      controlledPropValue: selectedTab,
 *      changeHandlerdefault: onSelectedTabChange
 *    }
 *  );
 *
 *  const handleClick = ({target}) => {
 *    * `setPropValue` method will call the `onSelectedChange` with `otherArgs`
 *    * as arguments after updating the state maintained in the hook.
 *    if(selectedTab !== null) {
 *       * Parent component will receive current value
 *       * and they can update it and pass to Framework component.
 *      setPropValue(getPropValue(), true, otherArgs);
 *    } else {
 *       * Framework component will amend the logic accordingly
 *       * send the updated value to the hook for updating it.
 *      setPropValue(target.getAttribute(id), true, otherArgs);
 *    }
 *  }
 *
 *  return (
 *    <div>
 *      Currently selected tab is {getPropValue()}
 *      <button id="1" onClick={handleClick}>Tab 1</button>
 *      <button id="2" onClick={handleClick}>Tab 2</button>
 *      <button id="3" onClick={handleClick}>Tab 3</button>
 *    </div>
 *  )
 * }
 *
 * * Uncontrolled way
 * <ParentComponent defaultSelectedTab={2}
 *   onSelectedTabChange={(val, otherArgs) => console.log(val, otherArgs)}
 * />
 *
 * * Controlled way
 * <ParentComponent selectedTab={selectedTabStateMaintainedInConsumingComponent}
 *   onSelectedTabChange={(val, otherArgs) => console.log(val, otherArgs)}
 * />
 *
 */

export default function useControlOrUnControl(_ref) {
  let {
    unControlledPropValue,
    controlledPropValue,
    changeHandler
  } = _ref;
  const [stateVal, setStateVal] = useState(unControlledPropValue);
  const syncValRef = useRef(true);
  const argsRef = useRef([]);
  useEffect(() => {
    /**
     * When setPropValue method is triggered, user can pass
     * the sync value but it is not available outside of the function
     * hence added reference for `sync` and `args` attribute.
     */
    if (!syncValRef.current) {
      changeHandler && changeHandler(stateVal, ...argsRef.current);
    }
  }, [stateVal, argsRef, changeHandler]);
  /**
   * Memoizing the value so even if `getPropValue` is called
   * multiple times in the component it does not execute
   * the condition till one of the dependencies changes.
   */

  const memoizedStateValue = useMemo(() =>
  /**
   * If prop[propName], then we have a Controlled value
   * otherwise, value is Uncontrolled (it comes from internal state)
   */
  controlledPropValue !== null ? controlledPropValue : stateVal, [controlledPropValue, stateVal]);
  /**
   * Gets the value of a Controllable/Uncontrollable property.
   * @return {Any} The value of the property.
   */

  const getPropValue = () => memoizedStateValue;
  /**
   * Sets the value of a Controllable prop and fires the onChange callback when provided
   * in either a sync or async way.
   * @param {Object}    value    The new value
   * @param {Boolean}   sync    Whether the callback is fired sync or async
   * @param {...[Object]} args     Any extra args to pass to the callback
   */


  const setPropValue = function (value, sync) {
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }

    /**
     * Setting Controlled value (value controlled by parent Component)
     * No need to Set the value. Just fire onPropChange callback to notify parent Component
     */
    if (controlledPropValue !== null && changeHandler) {
      changeHandler(value, ...args);
    } else {
      /**
       * We have the same value so do not set the state again & call the onChange callback handler.
       */
      if (stateVal === value) {
        return;
      }
      /**
       * Setting Uncontrolled value (value controlled by internal state)
       * Set the value to the state and fire onPropChange callback to notify the parent Component
       * onPropChange callback can be fire sync or async (after state is set)
       */


      setStateVal(value);
      /**
       * If sync value is false meaning the consumer wants the
       * change handler to run after the internal state is update hence
       * calling the change handler in the `useEffect` hook.
       */

      syncValRef.current = sync;
      argsRef.current = args;
      sync && changeHandler && changeHandler(value, ...args);
    }
  };

  return [getPropValue, setPropValue];
}